import { EmptyState } from '@patchbay/ui'
import { createFileRoute, Link, redirect, useRouter } from '@tanstack/react-router'
import * as React from 'react'
import { Frame } from '../components/frame'
import { resolveSession } from '../features/auth/session'
import {
  CLOSED_DRILL,
  LicencesScreen,
  ProjectsReportScreen,
  parseReportsSearch,
  ReportScreen,
  type ReportsSearch,
  reportQuery,
  reportRouteKind,
  SuiteReportScreen,
} from '../features/reports'
import { isProjectReport } from '../features/reports/projects/keys'
import { canSeeLicences } from '../features/reports/roles'

/** Set in the history state of the entry the reports page pushed to open a drill, so closing it can go Back instead. */
const drillPushed = (state: object): boolean => 'reportsDrillPushed' in state && state.reportsDrillPushed === true

export const Route = createFileRoute('/reports/$reportId')({
  validateSearch: (raw: Record<string, unknown>): ReportsSearch => parseReportsSearch(raw),
  // Licence margin is manager and billing only (the API's COST_READERS); anyone else goes to the Reports home. An unknown
  // session (API unreachable) is not a role, so it is not redirected: the screen shows what failed instead.
  beforeLoad: async ({ context, params }) => {
    if (reportRouteKind(params.reportId).kind !== 'licences') return
    const session = await resolveSession(context.queryClient)
    if (session.kind === 'signed_in' && !canSeeLicences(session.me.role)) throw redirect({ to: '/reports' })
  },
  loader: ({ context, params }) => {
    const route = reportRouteKind(params.reportId)
    // #309's scheduled report keeps its suspense loader; suite reports load in the screen (filters live in the search).
    if (route.kind === 'scheduled') return context.queryClient.ensureQueryData(reportQuery(route.id))
    return undefined
  },
  component: function ReportRoute() {
    const { reportId } = Route.useParams()
    const search = Route.useSearch()
    const navigate = Route.useNavigate()
    const router = useRouter()
    const onSearch = React.useCallback(
      (patch: Partial<ReportsSearch>) =>
        // Filters replace the entry; opening a drill pushes one (marked in its history state), so Back closes the drawer.
        void navigate({
          search: (prev: ReportsSearch) => parseReportsSearch({ ...prev, ...patch }),
          replace: !patch.drill,
          state: (prev) => {
            const next = { ...prev, reportsDrillPushed: patch.drill ? true : 'drill' in patch ? false : drillPushed(prev) }
            return next
          },
        }),
      [navigate],
    )
    // Closing a drill the app pushed goes Back (no dead Back step left behind); a drill opened from a link replaces.
    const onCloseDrill = React.useCallback(() => {
      if (drillPushed(router.state.location.state) && router.history.canGoBack()) router.history.back()
      else onSearch(CLOSED_DRILL)
    }, [router, onSearch])
    const route = reportRouteKind(reportId)
    if (route.kind === 'scheduled') return <ReportScreen id={route.id} />
    if (route.kind === 'licences') return <LicencesScreen />
    if (route.kind === 'native' && isProjectReport(route.key))
      return <ProjectsReportScreen reportKey={route.key} search={search} onSearch={onSearch} onCloseDrill={onCloseDrill} />
    if (route.kind === 'native')
      return <SuiteReportScreen reportKey={route.key} search={search} onSearch={onSearch} onCloseDrill={onCloseDrill} />
    return (
      <Frame
        active="reports"
        railless
        main={
          <EmptyState
            title="No such report."
            detail={`"${reportId}" is not a report Patchbay knows.`}
            action={
              <Link to="/reports" className="text-sm font-medium text-primary hover:underline">
                Back to Reports
              </Link>
            }
          />
        }
      />
    )
  },
})
